/**
 * Canonical back-navigation link for ANY page across the platform —
 * admin tools, citizen-facing pages, static pages, forms, everything.
 *
 * Standardised look (Henaket frameless text-button pattern):
 *   - Default: filled left-pointing triangle + label, color
 *     --color-link, no underline
 *   - Hover: 3px thick underline appears (color unchanged)
 *   - Pressed: magenta #bd13b8 background fill, white text + white
 *     triangle, outline suppressed
 *   - Focus: existing platform-wide magenta outline ring (unchanged)
 *
 * Two modes:
 *   - `to="/path"` for React Router navigation (the common case)
 *   - `onClick={fn}` for in-component state transitions (e.g. closing
 *     an editor view that's a sub-state rather than a route)
 *
 * `className` is optional and appended to the internal class — use
 * it for positioning overrides (e.g. `margin-left: auto` to right-
 * align the link inside a flex action row). DO NOT use it to change
 * the visual look — the Henaket pattern is fixed.
 *
 * Single source of truth for every back-link on the platform. The
 * previous per-page implementations (StaticPage.returnLink,
 * CafRegister.backLink, plus 13 admin module.css files) have all
 * been removed in favor of this component.
 */
import { Link } from 'react-router-dom';
import styles from './BackLink.module.css';

// Filled left-pointing triangle, 10x10, currentColor. SVG (not unicode)
// so size and color render identically across fonts/OSes — including
// flipping to white when the global .linkButton:active rule fires.
const Triangle = () => (
  <svg
    className={styles.triangle}
    viewBox="0 0 10 10"
    aria-hidden="true"
  >
    <path d="M 8 0 L 0 5 L 8 10 Z" fill="currentColor" />
  </svg>
);

export default function BackLink({ to, onClick, children, className }) {
  const combined = className
    ? `${styles.linkButton} ${className}`
    : styles.linkButton;

  if (onClick && !to) {
    return (
      <button type="button" onClick={onClick} className={combined}>
        <Triangle />
        <span className={styles.label}>{children}</span>
      </button>
    );
  }
  return (
    <Link to={to} className={combined}>
      <Triangle />
      <span className={styles.label}>{children}</span>
    </Link>
  );
}
